'use client';

import { c as _c } from "react/compiler-runtime";
import 'client-only';
import { useCallback, useEffect, useRef, useState, type PropsWithChildren } from 'react';
import mermaid, { type MermaidConfig } from 'mermaid';
import { useDarkMode } from '../hooks/useDarkMode';
import type { MermaidProps } from './Mermaid';
const DEFAULT_CONFIG: MermaidConfig = {
  startOnLoad: false,
  theme: 'default',
  logLevel: 'error',
  securityLevel: 'loose',
  arrowMarkerAbsolute: false,
  flowchart: {
    htmlLabels: true,
    curve: 'linear'
  },
  sequence: {
    diagramMarginX: 50,
    diagramMarginY: 10,
    actorMargin: 50,
    width: 120,
    height: 30,
    boxMargin: 10,
    boxTextMargin: 5,
    noteMargin: 10,
    messageMargin: 35,
    mirrorActors: true,
    bottomMarginAdj: 1,
    useMaxWidth: true,
    rightAngles: false,
    showSequenceNumbers: false
  },
  gantt: {
    titleTopMargin: 25,
    barHeight: 16,
    barGap: 4,
    topPadding: 50,
    leftPadding: 75,
    gridLineStartPadding: 35,
    fontSize: 11,
    numberSectionStyles: 4,
    axisFormat: '%Y-%m-%d'
  }
} as const;
export default function MermaidInner(t0) {
  const $ = _c(17);
  const {
    children,
    config
  } = t0;
  const [isRendered, setIsRendered] = useState(false);
  const mermaidDiv = useRef(null);
  const isDarkMode = useDarkMode();
  let t1;
  let t2;
  if ($[0] !== config || $[1] !== isDarkMode) {
    t1 = () => {
      mermaid.initialize({
        ...DEFAULT_CONFIG,
        ...config,
        theme: isDarkMode ? "dark" : "default"
      });
    };
    t2 = [config, isDarkMode];
    $[0] = config;
    $[1] = isDarkMode;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = async () => {
      if (!mermaidDiv.current) {
        throw new Error("Mermaid div not found");
      }
      await mermaid.run({
        nodes: [mermaidDiv.current]
      });
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const runMermaid = t3;
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      runMermaid().then(() => {
        setIsRendered(true);
      });
      return () => {
        setIsRendered(false);
      };
    };
    t5 = [runMermaid];
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== isRendered) {
    t6 = isRendered ? "" : <div>Rendering...{" "}<span className="appear-10s">it looks like something went wrong?</span></div>;
    $[7] = isRendered;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <span>{t6}</span>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = isRendered ? "" : "hidden";
  let t9;
  if ($[11] !== children || $[12] !== t8) {
    t9 = <div className={t8} ref={mermaidDiv}>{children}</div>;
    $[11] = children;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7 || $[15] !== t9) {
    t10 = <>{t7}{t9}</>;
    $[14] = t7;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
}